import { t } from "@/lib/i18n";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GlassSelect } from "@/components/ui/select";
import { Segmented } from "@/components/ui/segmented";
import { Tooltip } from "@/components/ui/tooltip";
import { Toggle } from "@/components/ui/toggle";
import { api } from "@/lib/api";
import { storageTargetType } from "@/lib/api/shared";
import type { StorageFieldIssue } from "@/lib/storage-validation";
import type {
  BaiduNetdiskStorageTargetConfig,
  CredentialRef,
  HttpStorageTargetConfig,
  Pan123OpenStorageTargetConfig,
  SftpStorageTargetConfig,
  StorageTargetConfig,
  StorageTargetKind,
  WebDavStorageTargetConfig,
} from "@/lib/types";
import {
  BAIDU_AUTH_MODE_OPTIONS,
  baiduNetdiskHint,
  localPublicBaseUrlHint,
  METHOD_OPTIONS,
  PAN123_AUTH_MODE_OPTIONS,
  pan123OpenHint,
  getStorageTargetTypeOptions,
} from "./constants";
import { CredentialEditor } from "./credential-editor";

function HintButton({
  text,
  ariaLabel = t("查看对接条件"),
}: {
  text: string;
  ariaLabel?: string;
}) {
  return (
    <Tooltip text={text}>
      <button
        type="button"
        aria-label={ariaLabel}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border text-[11px] font-semibold text-muted transition-colors hover:border-[color:var(--accent-45)] hover:text-foreground"
      >
        ?
      </button>
    </Tooltip>
  );
}

function issueForField(issues: StorageFieldIssue[], field: string) {
  return issues.find((issue) => issue.field === field)?.message;
}

function StorageField({
  label,
  hint,
  description,
  error,
  required,
  children,
}: {
  label?: string;
  hint?: ReactNode;
  description?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  // No label means there's nothing for the required star or hint to attach
  // to; rendering them alone produced an empty leading row that knocked the
  // grid baseline off (the lone "*" you saw above S3 bucket / WebDAV URL).
  // For label-less fields, required-state is communicated via the input's
  // invalid border + the trailing error text, which is sufficient.
  const showLabelRow = Boolean(label);
  return (
    <div className="space-y-1">
      {showLabelRow && (
        <div className="flex min-h-5 items-center gap-1.5 text-[11.5px] font-medium text-muted">
          <span>{label}</span>
          {required && (
            <span
              className="text-[color:var(--accent-70)]"
              aria-label={t("必填")}
            >
              *
            </span>
          )}
          {hint}
        </div>
      )}
      {children}
      {description && (
        <p className="text-[11px] leading-snug text-faint">{description}</p>
      )}
      {error && (
        <div className="text-[11px] leading-snug text-status-err">{error}</div>
      )}
    </div>
  );
}

export function StorageTargetCard({
  name,
  target,
  issues = [],
  testPending,
  onRename,
  onSetType,
  onPatch,
  onRemove,
  onRunTest,
  onAddHttpHeader,
  onUpdateHttpHeader,
}: {
  name: string;
  target: StorageTargetConfig;
  issues?: StorageFieldIssue[];
  testPending: boolean;
  onRename: (name: string, nextName: string) => void;
  onSetType: (name: string, type: StorageTargetKind) => void;
  onPatch: (
    name: string,
    next: Partial<StorageTargetConfig> | StorageTargetConfig,
  ) => void;
  onRemove: (name: string) => void;
  onRunTest: (name: string) => void;
  onAddHttpHeader: (name: string) => void;
  onUpdateHttpHeader: (
    name: string,
    header: string,
    nextHeader: string,
    credential: CredentialRef | null,
  ) => void;
}) {
  const type = storageTargetType(target);
  const localDirectoryLabel =
    api.kind === "http" ? t("服务器目录") : t("本地目录");
  const webdavTarget =
    type === "webdav" ? (target as WebDavStorageTargetConfig) : undefined;
  const httpTarget =
    type === "http" ? (target as HttpStorageTargetConfig) : undefined;
  const sftpTarget =
    type === "sftp" ? (target as SftpStorageTargetConfig) : undefined;
  const baiduTarget =
    type === "baidu_netdisk"
      ? (target as BaiduNetdiskStorageTargetConfig)
      : undefined;
  const pan123Target =
    type === "pan123_open"
      ? (target as Pan123OpenStorageTargetConfig)
      : undefined;
  const baiduAuthMode =
    baiduTarget?.auth_mode === "oauth" ? "oauth" : "personal";
  const pan123AuthMode =
    pan123Target?.auth_mode === "access_token" ? "access_token" : "client";
  const fieldError = (field: string) => issueForField(issues, field);

  return (
    <div className="space-y-2 rounded-lg border border-border bg-[color:var(--w-03)] p-3">
      <div className="grid gap-2 sm:grid-cols-[minmax(180px,220px)_minmax(180px,1fr)_auto] sm:items-center">
        <input
          defaultValue={name}
          onBlur={(event) => onRename(name, event.target.value)}
          aria-label={t("上传位置名称")}
          className="h-7 w-full rounded-md border border-border bg-[color:var(--w-04)] px-2.5 font-mono text-[13px] outline-none transition-colors placeholder:text-faint focus:border-[color:var(--accent-55)] focus:bg-[color:var(--accent-06)] focus:shadow-[0_0_0_3px_var(--accent-14)]"
        />
        <div className="flex min-w-0 items-center gap-2">
          <GlassSelect
            value={type}
            onValueChange={(value) =>
              onSetType(name, value as StorageTargetKind)
            }
            options={getStorageTargetTypeOptions(api.kind)}
            size="sm"
            ariaLabel={t("上传位置类型")}
            className="w-full"
          />
          {type === "baidu_netdisk" && <HintButton text={baiduNetdiskHint()} />}
          {type === "pan123_open" && <HintButton text={pan123OpenHint()} />}
        </div>
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            icon="play"
            disabled={testPending}
            onClick={() => onRunTest(name)}
          >
            {t("测试")}
          </Button>
          <Button
            variant="ghost"
            size="iconSm"
            icon="trash"
            onClick={() => onRemove(name)}
            aria-label={t("删除上传位置")}
          />
        </div>
      </div>
      {type === "local" && "directory" in target && (
        <div className="grid gap-3 md:grid-cols-2">
          <StorageField
            label={localDirectoryLabel}
            error={fieldError("directory")}
            description={
              api.kind === "http"
                ? t("服务器上的路径；docker 部署需挂载为 volume。")
                : undefined
            }
            required
          >
            <Input
              value={target.directory}
              onChange={(event) =>
                onPatch(name, { directory: event.target.value })
              }
              placeholder={
                api.kind === "http"
                  ? "/data/gpt-image-2/extra-archive"
                  : "/path/to/storage"
              }
              size="sm"
              aria-label={localDirectoryLabel}
              aria-invalid={Boolean(fieldError("directory"))}
              suffix={
                api.canChooseExportFolder ? (
                  <Button
                    variant="ghost"
                    size="iconSm"
                    icon="folder"
                    className="h-6 w-6 shrink-0 text-foreground"
                    title={t("浏览文件夹")}
                    aria-label={t("浏览{p0}", { p0: localDirectoryLabel })}
                    onClick={async () => {
                      const picked = await api.chooseFolder?.(
                        "directory" in target ? target.directory : undefined,
                      );
                      if (picked) onPatch(name, { directory: picked });
                    }}
                  >
                    <span className="sr-only">
                      {t("浏览{p0}", { p0: localDirectoryLabel })}
                    </span>
                  </Button>
                ) : undefined
              }
            />
          </StorageField>
          <StorageField
            label={t("公开访问前缀")}
            hint={
              <HintButton
                text={localPublicBaseUrlHint()}
                ariaLabel={t("查看公开访问前缀说明")}
              />
            }
            description={t("用于生成可访问图片 URL；没有静态访问服务时留空。")}
          >
            <Input
              value={target.public_base_url ?? ""}
              onChange={(event) =>
                onPatch(name, { public_base_url: event.target.value })
              }
              placeholder="https://cdn.example.com/images"
              size="sm"
              aria-label={t("公开访问前缀")}
            />
          </StorageField>
        </div>
      )}
      {type === "s3" && "bucket" in target && (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-3">
            <StorageField error={fieldError("bucket")} required>
              <Input
                value={target.bucket}
                onChange={(event) =>
                  onPatch(name, { bucket: event.target.value })
                }
                placeholder="bucket"
                size="sm"
                aria-label="S3 bucket"
                aria-invalid={Boolean(fieldError("bucket"))}
              />
            </StorageField>
            <Input
              value={target.region ?? ""}
              onChange={(event) =>
                onPatch(name, { region: event.target.value })
              }
              placeholder="region"
              size="sm"
              aria-label="S3 region"
            />
            <Input
              value={target.prefix ?? ""}
              onChange={(event) =>
                onPatch(name, { prefix: event.target.value })
              }
              placeholder="prefix/"
              size="sm"
              aria-label="S3 prefix"
            />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              value={target.endpoint ?? ""}
              onChange={(event) =>
                onPatch(name, { endpoint: event.target.value })
              }
              placeholder="S3 endpoint"
              size="sm"
              aria-label="S3 endpoint"
            />
            <Input
              value={target.public_base_url ?? ""}
              onChange={(event) =>
                onPatch(name, { public_base_url: event.target.value })
              }
              placeholder={t("对外访问前缀（可选）")}
              size="sm"
              aria-label={t("S3 对外访问前缀")}
            />
          </div>
          <StorageField error={fieldError("access_key_id")} required>
            <CredentialEditor
              credential={target.access_key_id}
              onChange={(access_key_id) => onPatch(name, { access_key_id })}
              placeholder="Access Key ID"
              ariaLabel="S3 Access Key ID"
              invalid={Boolean(fieldError("access_key_id"))}
            />
          </StorageField>
          <StorageField error={fieldError("secret_access_key")} required>
            <CredentialEditor
              credential={target.secret_access_key}
              onChange={(secret_access_key) =>
                onPatch(name, { secret_access_key })
              }
              placeholder="Secret Access Key"
              ariaLabel="S3 Secret Access Key"
              invalid={Boolean(fieldError("secret_access_key"))}
            />
          </StorageField>
        </div>
      )}
      {webdavTarget && (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <StorageField error={fieldError("url")} required>
              <Input
                value={webdavTarget.url}
                onChange={(event) => onPatch(name, { url: event.target.value })}
                placeholder="https://dav.example.com/out"
                size="sm"
                aria-label="WebDAV URL"
                aria-invalid={Boolean(fieldError("url"))}
              />
            </StorageField>
            <Input
              value={webdavTarget.public_base_url ?? ""}
              onChange={(event) =>
                onPatch(name, { public_base_url: event.target.value })
              }
              placeholder={t("对外访问前缀（可选）")}
              size="sm"
              aria-label={t("WebDAV 对外访问前缀")}
            />
          </div>
          <Input
            value={webdavTarget.username ?? ""}
            onChange={(event) =>
              onPatch(name, { username: event.target.value })
            }
            placeholder="username"
            size="sm"
            aria-label="WebDAV username"
          />
          <CredentialEditor
            credential={webdavTarget.password}
            onChange={(password) => onPatch(name, { password })}
            placeholder="password"
            ariaLabel="WebDAV password"
          />
        </div>
      )}
      {httpTarget && (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_110px_150px]">
            <StorageField error={fieldError("url")} required>
              <Input
                value={httpTarget.url}
                onChange={(event) => onPatch(name, { url: event.target.value })}
                placeholder="https://upload.example.com"
                size="sm"
                aria-label="HTTP upload URL"
                aria-invalid={Boolean(fieldError("url"))}
              />
            </StorageField>
            <GlassSelect
              value={httpTarget.method || "POST"}
              onValueChange={(method) => onPatch(name, { method })}
              options={METHOD_OPTIONS}
              size="sm"
              ariaLabel="HTTP method"
            />
            <Input
              value={httpTarget.public_url_json_pointer ?? ""}
              onChange={(event) =>
                onPatch(name, {
                  public_url_json_pointer: event.target.value,
                })
              }
              placeholder="/data/url"
              size="sm"
              aria-label={t("JSON 中公开 URL 的字段路径")}
            />
          </div>
          {Object.entries(httpTarget.headers ?? {}).map(
            ([header, credential]) => (
              <div
                key={`${name}:${header}`}
                className="grid gap-2 sm:grid-cols-[150px_minmax(0,1fr)_32px]"
              >
                <Input
                  value={header}
                  onChange={(event) =>
                    onUpdateHttpHeader(
                      name,
                      header,
                      event.target.value,
                      credential,
                    )
                  }
                  placeholder="Authorization"
                  size="sm"
                  monospace
                  aria-label="HTTP header"
                />
                <CredentialEditor
                  credential={credential}
                  onChange={(nextCredential) =>
                    onUpdateHttpHeader(name, header, header, nextCredential)
                  }
                  placeholder="Bearer ..."
                  ariaLabel={t("{p0} 值", { p0: header })}
                />
                <Button
                  variant="ghost"
                  size="iconSm"
                  icon="x"
                  onClick={() => onUpdateHttpHeader(name, header, "", null)}
                  aria-label={t("删除 HTTP header")}
                />
              </div>
            ),
          )}
          <Button
            variant="ghost"
            size="sm"
            icon="plus"
            onClick={() => onAddHttpHeader(name)}
          >
            {t("添加 Header")}
          </Button>
        </div>
      )}
      {sftpTarget && (
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_88px_minmax(0,1fr)]">
            <StorageField error={fieldError("host")} required>
              <Input
                value={sftpTarget.host}
                onChange={(event) =>
                  onPatch(name, { host: event.target.value })
                }
                placeholder="host"
                size="sm"
                aria-label="SFTP host"
                aria-invalid={Boolean(fieldError("host"))}
              />
            </StorageField>
            <Input
              value={String(sftpTarget.port || 22)}
              onChange={(event) =>
                onPatch(name, { port: Number(event.target.value) || 22 })
              }
              inputMode="numeric"
              size="sm"
              aria-label="SFTP port"
            />
            <StorageField error={fieldError("username")} required>
              <Input
                value={sftpTarget.username}
                onChange={(event) =>
                  onPatch(name, { username: event.target.value })
                }
                placeholder="username"
                size="sm"
                aria-label="SFTP username"
                aria-invalid={Boolean(fieldError("username"))}
              />
            </StorageField>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <StorageField error={fieldError("remote_dir")} required>
              <Input
                value={sftpTarget.remote_dir}
                onChange={(event) =>
                  onPatch(name, { remote_dir: event.target.value })
                }
                placeholder="/remote/out"
                size="sm"
                aria-label="SFTP remote dir"
                aria-invalid={Boolean(fieldError("remote_dir"))}
              />
            </StorageField>
            <Input
              value={sftpTarget.public_base_url ?? ""}
              onChange={(event) =>
                onPatch(name, { public_base_url: event.target.value })
              }
              placeholder={t("对外访问前缀（可选）")}
              size="sm"
              aria-label={t("SFTP 对外访问前缀")}
            />
          </div>
          <StorageField error={fieldError("host_key_sha256")} required>
            <Input
              value={sftpTarget.host_key_sha256 ?? ""}
              onChange={(event) =>
                onPatch(name, { host_key_sha256: event.target.value })
              }
              placeholder={t("SHA256 指纹")}
              size="sm"
              aria-label={t("SFTP 服务器 SHA256 指纹")}
              aria-invalid={Boolean(fieldError("host_key_sha256"))}
            />
          </StorageField>
          <StorageField error={fieldError("sftp_auth")} required>
            <CredentialEditor
              credential={sftpTarget.password}
              onChange={(password) => onPatch(name, { password })}
              placeholder="password"
              ariaLabel="SFTP password"
              invalid={Boolean(fieldError("sftp_auth"))}
            />
          </StorageField>
          <CredentialEditor
            credential={sftpTarget.private_key}
            onChange={(private_key) => onPatch(name, { private_key })}
            placeholder="private key"
            ariaLabel="SFTP private key"
            invalid={Boolean(fieldError("sftp_auth"))}
          />
        </div>
      )}
      {baiduTarget && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              value={baiduAuthMode}
              onChange={(auth_mode) => onPatch(name, { auth_mode })}
              options={BAIDU_AUTH_MODE_OPTIONS}
              size="sm"
              ariaLabel={t("百度网盘对接方式")}
            />
            <span className="text-[11px] text-faint">
              {baiduAuthMode === "personal"
                ? t("个人对接只需要长期 Access Token。")
                : t("OAuth 对接使用应用凭证换取访问令牌。")}
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <StorageField error={fieldError("app_name")} required>
              <Input
                value={baiduTarget.app_name}
                onChange={(event) =>
                  onPatch(name, { app_name: event.target.value })
                }
                placeholder={t("应用目录名")}
                size="sm"
                aria-label={t("百度网盘应用目录名")}
                aria-invalid={Boolean(fieldError("app_name"))}
              />
            </StorageField>
            <Input
              value={baiduTarget.remote_dir ?? ""}
              onChange={(event) =>
                onPatch(name, { remote_dir: event.target.value })
              }
              placeholder="outputs"
              size="sm"
              aria-label={t("百度网盘远端目录")}
            />
          </div>
          <Input
            value={baiduTarget.public_base_url ?? ""}
            onChange={(event) =>
              onPatch(name, { public_base_url: event.target.value })
            }
            placeholder={t("公开基础 URL（可选）")}
            size="sm"
            aria-label={t("百度网盘公开基础 URL")}
          />
          {baiduAuthMode === "personal" && (
            <StorageField error={fieldError("access_token")} required>
              <CredentialEditor
                credential={baiduTarget.access_token}
                onChange={(access_token) => onPatch(name, { access_token })}
                placeholder="Access Token"
                ariaLabel={t("百度网盘 Access Token")}
                invalid={Boolean(fieldError("access_token"))}
              />
            </StorageField>
          )}
          {baiduAuthMode === "oauth" && (
            <div className="space-y-2">
              <StorageField error={fieldError("app_key")} required>
                <Input
                  value={baiduTarget.app_key}
                  onChange={(event) =>
                    onPatch(name, { app_key: event.target.value })
                  }
                  placeholder="App Key"
                  size="sm"
                  aria-label={t("百度网盘 App Key")}
                  aria-invalid={Boolean(fieldError("app_key"))}
                  suffix={<HintButton text={baiduNetdiskHint()} />}
                />
              </StorageField>
              <StorageField error={fieldError("secret_key")} required>
                <CredentialEditor
                  credential={baiduTarget.secret_key}
                  onChange={(secret_key) => onPatch(name, { secret_key })}
                  placeholder="Secret Key"
                  ariaLabel={t("百度网盘 Secret Key")}
                  invalid={Boolean(fieldError("secret_key"))}
                />
              </StorageField>
              <StorageField error={fieldError("refresh_token")} required>
                <CredentialEditor
                  credential={baiduTarget.refresh_token}
                  onChange={(refresh_token) => onPatch(name, { refresh_token })}
                  placeholder="Refresh Token"
                  ariaLabel={t("百度网盘 Refresh Token")}
                  invalid={Boolean(fieldError("refresh_token"))}
                />
              </StorageField>
            </div>
          )}
        </div>
      )}
      {pan123Target && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              value={pan123AuthMode}
              onChange={(auth_mode) => onPatch(name, { auth_mode })}
              options={PAN123_AUTH_MODE_OPTIONS}
              size="sm"
              ariaLabel={t("123 网盘对接方式")}
            />
            <span className="text-[11px] text-faint">
              {pan123AuthMode === "client"
                ? t("client 对接使用 clientID + clientSecret。")
                : t("accessToken 对接只需要长期 accessToken。")}
            </span>
          </div>
          <Input
            value={String(pan123Target.parent_id || 0)}
            onChange={(event) =>
              onPatch(name, { parent_id: Number(event.target.value) || 0 })
            }
            inputMode="numeric"
            size="sm"
            aria-label={t("123 网盘父目录 ID")}
          />
          <div className="flex items-center gap-2 rounded-md border border-border bg-[color:var(--w-04)] px-3 py-2 text-[12px] text-muted">
            <Toggle
              checked={pan123Target.use_direct_link}
              onChange={(use_direct_link) => onPatch(name, { use_direct_link })}
              label={t("上传后尝试获取直链")}
            />
            <HintButton text={pan123OpenHint()} />
          </div>
          {pan123AuthMode === "client" && (
            <div className="space-y-2">
              <StorageField error={fieldError("client_id")} required>
                <Input
                  value={pan123Target.client_id}
                  onChange={(event) =>
                    onPatch(name, { client_id: event.target.value })
                  }
                  placeholder="clientID"
                  size="sm"
                  aria-label={t("123 网盘 clientID")}
                  aria-invalid={Boolean(fieldError("client_id"))}
                  suffix={<HintButton text={pan123OpenHint()} />}
                />
              </StorageField>
              <StorageField error={fieldError("client_secret")} required>
                <CredentialEditor
                  credential={pan123Target.client_secret}
                  onChange={(client_secret) => onPatch(name, { client_secret })}
                  placeholder="clientSecret"
                  ariaLabel={t("123 网盘 clientSecret")}
                  invalid={Boolean(fieldError("client_secret"))}
                />
              </StorageField>
            </div>
          )}
          {pan123AuthMode === "access_token" && (
            <StorageField error={fieldError("access_token")} required>
              <CredentialEditor
                credential={pan123Target.access_token}
                onChange={(access_token) => onPatch(name, { access_token })}
                placeholder="accessToken"
                ariaLabel={t("123 网盘 accessToken")}
                invalid={Boolean(fieldError("access_token"))}
              />
            </StorageField>
          )}
        </div>
      )}
    </div>
  );
}
